import React, { useEffect, useState } from 'react';
import { Drawer, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle, DrawerTrigger } from '../shadcn-ui/drawer';
import { Button } from '../shadcn-ui/button';
import { Loader2, Pencil } from 'lucide-react';
import { AddStorageLotSchema } from '@/schema/storage-lot-schema';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import FormField from '../FormField';
import { updateStorageLot } from '@/network/api.service';
import { toast } from 'sonner';

const UpdateStorageLotDrawer = ({ lot, fetchStorageDetailLots }) => {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(AddStorageLotSchema),
    defaultValues: {
      lot_id: lot?.lot_id || '',
      lot_number: lot?.lot_number || '',
      date_of_storage: lot?.date_of_storage ? new Date(lot.date_of_storage) : new Date(),
      moisture_1: lot?.moisture_1 ?? '',
      moisture_2: lot?.moisture_2 ?? '',
      bags: lot?.bags ?? '',
      total_weight: lot?.total_weight ?? '',
      comments: lot?.comments || '',
    },
  });

  useEffect(() => {
    if (lot) {
      reset({
        lot_id: lot.lot_id,
        lot_number: lot.lot_number,
        date_of_storage: lot?.date_of_storage ? new Date(lot.date_of_storage) : new Date(),
        moisture_1: lot?.moisture_1 ?? '',
        moisture_2: lot?.moisture_2 ?? '',
        bags: lot?.bags ?? '',
        total_weight: lot?.total_weight ?? '',
        comments: lot?.comments || '',
      });
    }
  }, [lot, reset]);

  const handleOpenChange = open => {
    if (!open) {
      reset();
    } else {
      // Set today's date when opening the drawer
      reset({
        lot_id: lot?.lot_id || '',
        lot_number: lot?.lot_number || '',
        date_of_storage: lot?.date_of_storage ? new Date(lot.date_of_storage) : new Date(),
        moisture_1: lot?.moisture_1 ?? '',
        moisture_2: lot?.moisture_2 ?? '',
        bags: lot?.bags ?? '',
        total_weight: lot?.total_weight ?? '',
        comments: lot?.comments || '',
      });
    }
    setOpen(open);
  };

  const onSubmit = async data => {
    try {
      setLoading(true);
      const { data: respData } = await updateStorageLot(data, lot?._id);
      if (respData?.success) {
        toast.success('Storage lot updated successfully');
        setOpen(false);
        reset();
        fetchStorageDetailLots();
      } else {
        toast.error('Failed to update storage lot');
      }
    } catch (error) {
      toast.error('Failed to update storage lot');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <button className="mr-1 flex-shrink-0">
          <Pencil className="h-5 w-5" />
        </button>
      </DrawerTrigger>

      <DrawerContent className="max-h-[90vh]">
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-1 flex-col gap-3 overflow-y-auto pb-5">
          <DrawerHeader className="text-left">
            <DrawerTitle>{lot?.storage_label || 'Update Storage Lot'}</DrawerTitle>
          </DrawerHeader>
          <div className="flex flex-col gap-3 px-5">
            <FormField
              date
              label="Date of storage"
              name="date_of_storage"
              required
              control={control}
              disabled={loading}
              errors={errors}
            />
            <FormField
              required
              type="number"
              step="0.01"
              label="Moisture 1 %"
              name="moisture_1"
              disabled={loading}
              placeholder="e.g., 4.2"
              className="bg-transparent"
              errors={errors}
              register={register}
            />
            <FormField
              type="number"
              label="Moisture 2 %"
              name="moisture_2"
              step="0.01"
              placeholder="e.g., 4.2"
              className="bg-transparent"
              register={register}
              disabled={loading}
              errors={errors}
            />
            <FormField
              type="number"
              label="Bags"
              name="bags"
              placeholder="e.g., 4"
              className="bg-transparent"
              register={register}
              disabled={loading}
              errors={errors}
            />
            <FormField
              type="number"
              step="0.01"
              label="Total weight"
              name="total_weight"
              placeholder="e.g., 100"
              className="bg-transparent"
              register={register}
              disabled={loading}
              errors={errors}
            />
            <FormField
              textarea
              label="Comments"
              name="comments"
              placeholder="Enter comments here..."
              className="bg-transparent"
              register={register}
              disabled={loading}
              errors={errors}
            />
          </div>

          <DrawerFooter className="flex flex-row justify-end gap-2 border-t">
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spinSlow" />}
              {loading ? 'Updating...' : 'Update lot'}
            </Button>
          </DrawerFooter>
        </form>
      </DrawerContent>
    </Drawer>
  );
};

export default UpdateStorageLotDrawer;
